import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { setMessage } from "./message";
import AuthService from "../services/auth.service";
import { AuthState } from "../interfaces/types";

type AuthUser = NonNullable<AuthState["user"]>;

interface RegisterPayload {
  username: string;
  email: string;
  password: string;
}

interface LoginPayload {
  username: string;
  password: string;
}

interface ForgotPasswordPayload {
  username: string;
}

interface ForgotPasswordUserPayload {
  username: string;
}

interface ResetPasswordPayload {
  token: string;
  email: string;
  password: string;
}

interface ResetPasswordUserPayload {
  token: string;
  username: string;
  password: string;
}

interface GetResetPasswordPayload {
  token: string;
}

const hasStoredToken = (): boolean => {
  if (localStorage.getItem("accessToken")) return true;

  try {
    const legacyUser = JSON.parse(localStorage.getItem("user") || "null");
    return !!legacyUser?.accessToken;
  } catch {
    return false;
  }
};

const getErrorMessage = (error: any): string =>
  error.response?.data?.message || error.message || error.toString();

export const register = createAsyncThunk(
  "auth/register",
  async (payload: RegisterPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.register(payload.username, payload.email, payload.password);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message = getErrorMessage(error);
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async (payload: LoginPayload, thunkAPI: any) => {
    try {
      await AuthService.login(payload.username, payload.password);
      return true;
    } catch (error: any) {
      const message = getErrorMessage(error);
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  "auth/me",
  async (_, thunkAPI: any) => {
    try {
      const response = await AuthService.getCurrentUser();
      return { user: response.data as AuthUser };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgot-password",
  async (payload: ForgotPasswordPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.forgotPassword(payload.username);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message = getErrorMessage(error);
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const forgotPasswordUser = createAsyncThunk(
  "auth/forgot-password-user",
  async (payload: ForgotPasswordUserPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.forgotPasswordUser(payload.username);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message = getErrorMessage(error);
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/reset-password",
  async (payload: ResetPasswordPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.resetPassword(payload.token, payload.email, payload.password);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message = getErrorMessage(error);
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const resetPasswordUser = createAsyncThunk(
  "auth/reset-password-user",
  async (payload: ResetPasswordUserPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.resetPasswordUser(payload.token, payload.username, payload.password);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message = getErrorMessage(error);
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const resetClientPassword = createAsyncThunk(
  "auth/reset-client-password",
  async (payload: ResetPasswordUserPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.resetClientPassword(payload.token, payload.username, payload.password);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message = getErrorMessage(error);
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getResetPassword = createAsyncThunk(
  "auth/get-reset-password",
  async (payload: GetResetPasswordPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.getResetPassword(payload.token);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message = getErrorMessage(error);
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const logout = createAsyncThunk("auth/logout", async () => {
  AuthService.logout();
});

const initialState: AuthState = {
  isLoggedIn: hasStoredToken(),
  user: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,

  reducers: {
    setUserConditions: (state, action: PayloadAction<boolean>) => {
      if (!state.user) return;
      state.user = { ...state.user, conditions: action.payload };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(register.fulfilled, (state) => {
        state.isLoggedIn = false;
      })
      .addCase(register.rejected, (state) => {
        state.isLoggedIn = false;
      })

      .addCase(login.fulfilled, (state) => {
        state.isLoggedIn = true;
        state.user = null;
      })
      .addCase(login.rejected, (state) => {
        AuthService.logout();
        state.isLoggedIn = false;
        state.user = null;
      })

      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        state.user = action.payload.user;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        AuthService.logout();
        state.isLoggedIn = false;
        state.user = null;
      })

      .addCase(logout.fulfilled, (state) => {
        state.isLoggedIn = false;
        state.user = null;
      })
      .addCase(logout.rejected, (state) => {
        state.isLoggedIn = false;
        state.user = null;
      })

      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoggedIn = false;
      })
      .addCase(forgotPassword.rejected, (state) => {
        state.isLoggedIn = false;
      })
      .addCase(forgotPasswordUser.fulfilled, () => {})
      .addCase(forgotPasswordUser.rejected, () => {})
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoggedIn = false;
      })
      .addCase(resetPassword.rejected, (state) => {
        state.isLoggedIn = false;
      })
      .addCase(resetPasswordUser.rejected, (state) => {
        state.isLoggedIn = false;
      })
      .addCase(resetClientPassword.rejected, (state) => {
        state.isLoggedIn = false;
      })
      .addCase(getResetPassword.fulfilled, () => {})
      .addCase(getResetPassword.rejected, () => {});
  },
});

export const { setUserConditions } = authSlice.actions;
export default authSlice.reducer;
